<template>
  <div
    v-if="show"
    :class="classes"
    role="alert"
  >
    <slot />
  </div>
</template>

<script setup lang="ts">
  /**
   * UiAlert — Tailwind primitive replacing BootstrapVue-Next's `b-alert`.
   *
   * Mirrors BVN's API: accepts a boolean `show` prop and a `variant`. When
   * `show` is false the alert isn't rendered (callers commonly combine
   * `show` with `v-if` for the same effect — both are safe).
   *
   * Variants use Tailwind's default colour scale (blue/green/yellow/red)
   * because the project's tailwind.config.js semantic tokens are flat
   * single-shade colours that don't read well as light alert backgrounds.
   */
  import { computed } from 'vue'

  defineOptions({ name: 'UiAlert' })

  interface Props {
    show?: boolean
    variant?: 'info' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger'
  }

  const props = withDefaults(defineProps<Props>(), {
    show: false,
    variant: 'info',
  })

  const VARIANTS: Record<NonNullable<Props['variant']>, string> = {
    info:      'tw-bg-blue-50 tw-border-blue-300 tw-text-blue-800',
    primary:   'tw-bg-blue-50 tw-border-blue-300 tw-text-blue-800',
    secondary: 'tw-bg-gray-50 tw-border-gray-300 tw-text-gray-800',
    success:   'tw-bg-green-50 tw-border-green-300 tw-text-green-800',
    warning:   'tw-bg-yellow-50 tw-border-yellow-300 tw-text-yellow-800',
    danger:    'tw-bg-red-50 tw-border-red-300 tw-text-red-800',
  }

  const BASE = 'tw-px-4 tw-py-3 tw-mb-4 tw-border tw-rounded-md'

  const classes = computed(() => `${BASE} ${VARIANTS[props.variant]}`)
</script>
